import { useQuery } from '@tanstack/react-query';
import { CalendarClock, Receipt } from 'lucide-react';
import { useState, type JSX } from 'react';

import { usePlatformPermissions } from '@/features/auth/hooks/usePlatformPermissions';

import { ExpiryAlertChip } from './ExpiryAlertChip';
import { PilotEndDialog } from './PilotEndDialog';
import { tenantBillingQueryKey } from './TenantBillingTab';
import styles from './TenantLifecyclePanel.module.scss';
import { TenantStatusDialog } from './TenantStatusDialog';
import {
  getTenantBillingProfile,
  type TenantDetail,
  type TenantLifecycleOption,
  type TenantStatusResult,
} from '../api/tenants.graphql';
import badgeStyles from '../pages/TenantsPage.module.scss';
import {
  BILLING_REQUIREMENT_LABELS,
  formatLifecycleDate,
  LIFECYCLE_ACTIONS,
  statusLabel,
} from '../tenant-labels';

interface Props {
  tenant: TenantDetail;
  onChanged: (result: TenantStatusResult) => void;
  /** OS-1722 — abre la pestaña Facturación (convertir a pago exige el perfil completo). */
  onGoToBilling?: () => void;
}

interface TimelineEntry {
  term: string;
  value: string;
}

/** Hitos del ciclo de vida que tiene el tenant, en orden cronológico. */
export function lifecycleTimeline(tenant: TenantDetail): TimelineEntry[] {
  const entries: (TimelineEntry | null)[] = [
    { term: 'Alta', value: formatLifecycleDate(tenant.createdAt) },
    tenant.pilotStartsAt
      ? { term: 'Inicio del piloto', value: formatLifecycleDate(tenant.pilotStartsAt) }
      : null,
    tenant.pilotEndsAt
      ? { term: 'Fin del piloto', value: formatLifecycleDate(tenant.pilotEndsAt) }
      : null,
    tenant.trialEndsAt && !tenant.pilotEndsAt
      ? { term: 'Fin del trial', value: formatLifecycleDate(tenant.trialEndsAt) }
      : null,
    tenant.convertedAt
      ? { term: 'Conversión a pago', value: formatLifecycleDate(tenant.convertedAt) }
      : null,
    tenant.cancelledAt
      ? { term: 'Cancelación', value: formatLifecycleDate(tenant.cancelledAt) }
      : null,
  ];
  return entries.filter((entry): entry is TimelineEntry => entry !== null);
}

/**
 * OS-1720 — estado comercial del tenant en la ficha: badge, alerta de vencimiento, línea de tiempo
 * y las acciones que el API permite al rol (la consola no repite la máquina de estados).
 */
export function TenantLifecyclePanel({ tenant, onChanged, onGoToBilling }: Props): JSX.Element {
  const { can } = usePlatformPermissions();
  // OS-1722 — si se puede convertir, se consulta si el perfil de facturación está completo.
  const canConvert = tenant.allowedLifecycleActions.some((a) => a.action === 'CONVERT');
  const billing = useQuery({
    queryKey: tenantBillingQueryKey(tenant.id),
    queryFn: () => getTenantBillingProfile(tenant.id),
    enabled: canConvert && can('platform.billing.read'),
  });
  const [billingMissing, setBillingMissing] = useState<string[] | null>(null);
  const [option, setOption] = useState<TenantLifecycleOption | null>(null);
  const [editingEnd, setEditingEnd] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const canChangePilotEnd =
    can('platform.tenants.suspend') && tenant.planStatus === 'pilot' && Boolean(tenant.pilotEndsAt);

  function done(result: TenantStatusResult, message: string): void {
    setOption(null);
    setEditingEnd(false);
    setNotice(message);
    onChanged(result);
  }

  return (
    <section className={styles.panel} aria-labelledby="tenant-lifecycle-title">
      <div className={styles.head}>
        <h2 id="tenant-lifecycle-title" className={styles.title}>
          Ciclo de vida
          <span className={`${badgeStyles.badge} ${badgeStyles[tenant.planStatus] ?? ''}`}>
            {statusLabel(tenant.planStatus)}
          </span>
          {tenant.expiryAlert ? <ExpiryAlertChip alert={tenant.expiryAlert} /> : null}
        </h2>
        <div className={styles.actions}>
          {tenant.allowedLifecycleActions.map((candidate) => {
            const view = LIFECYCLE_ACTIONS[candidate.action];
            return (
              <button
                key={candidate.action}
                type="button"
                className={`${styles.actionBtn} ${candidate.action === 'CONVERT' ? styles.primary : ''} ${view.danger ? styles.danger : ''}`}
                onClick={() => {
                  setNotice(null);
                  setBillingMissing(null);
                  // El API lo exige igual; aquí se evita abrir un diálogo que va a fallar.
                  if (candidate.action === 'CONVERT' && billing.data && !billing.data.complete) {
                    setBillingMissing(billing.data.missing);
                    return;
                  }
                  setOption(candidate);
                }}
              >
                {view.label}
              </button>
            );
          })}
          {canChangePilotEnd ? (
            <button
              type="button"
              className={styles.actionBtn}
              onClick={() => {
                setNotice(null);
                setEditingEnd(true);
              }}
            >
              <CalendarClock size={14} aria-hidden="true" />
              Cambiar fin del piloto
            </button>
          ) : null}
        </div>
      </div>

      <ul className={styles.timeline} aria-label="Línea de tiempo">
        {lifecycleTimeline(tenant).map((entry) => (
          <li key={entry.term}>
            <span className={styles.term}>{entry.term}</span>
            <span className={styles.value}>{entry.value}</span>
          </li>
        ))}
      </ul>
      {tenant.planStatus === 'cancelled' && tenant.cancelReason ? (
        <p className={styles.reason}>Motivo de la cancelación: {tenant.cancelReason}</p>
      ) : null}
      {tenant.expiryAlert?.level === 'EXPIRED' ? (
        <p className={styles.reason}>
          El piloto venció y el tenant sigue operando: conviértelo a pago, cambia la fecha de fin o
          cancélalo.
        </p>
      ) : null}
      {billingMissing ? (
        <div className={styles.reason} role="alert">
          <p className={styles.reason}>
            Para convertir a pago completa el perfil de facturación (falta:{' '}
            {billingMissing.map((key) => BILLING_REQUIREMENT_LABELS[key] ?? key).join(', ')}).
          </p>
          {onGoToBilling ? (
            <button
              type="button"
              className={`${styles.actionBtn} ${styles.primary}`}
              onClick={() => {
                setBillingMissing(null);
                onGoToBilling();
              }}
            >
              <Receipt size={14} aria-hidden="true" />
              Completar facturación
            </button>
          ) : null}
        </div>
      ) : null}
      <p className={styles.notice} role="status">
        {notice}
      </p>

      {option ? (
        <TenantStatusDialog
          tenant={tenant}
          option={option}
          onDone={(result) =>
            done(result, `Estado actualizado a ${statusLabel(result.planStatus).toLowerCase()}.`)
          }
          onCancel={() => setOption(null)}
        />
      ) : null}
      {editingEnd && tenant.pilotEndsAt ? (
        <PilotEndDialog
          tenant={{ id: tenant.id, name: tenant.name, pilotEndsAt: tenant.pilotEndsAt }}
          onDone={(result) =>
            done(
              result,
              result.pilotEndsAt
                ? `Nuevo fin del piloto: ${formatLifecycleDate(result.pilotEndsAt)}.`
                : 'Fecha de fin actualizada.',
            )
          }
          onCancel={() => setEditingEnd(false)}
        />
      ) : null}
    </section>
  );
}
